import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type BaseErrorOptions } from '@canvas/error';

import { DelegatingApolloClient } from '../delegating-apollo-client';
import { type DelegatingClient } from '../delegating-client-types';
import { DelegatingGraphQLClient } from '../delegating-graphql-client';
import { GraphQLClientError, throwOnQueryError } from '../graphql-response';

import {
  type OrganizationIdQuery,
  type OrganizationIdQueryInput,
  type UserProfileIdentity,
  type UsersQuery,
  type UsersQueryInput,
} from './identity-client-types';
import * as IdentityQueryOrganizationId from './queries/identity-query-organization-id.graphql';
import * as IdentityQueryUsers from './queries/identity-query-users.graphql';

export class IdentityGraphqlFetchError extends GraphQLClientError {
  constructor(message: string, options?: BaseErrorOptions) {
    super(message, { name: 'IdentityGraphqlFetchError', ...options });
  }
}

export class IdentityClient {
  private readonly clients: DelegatingClient = this.createClients();
  private readonly origin = new URL(
    globalThis.location ? globalThis.location.origin : ''
  );

  constructor() {}

  private createClients(): DelegatingClient {
    if (isFeatureFlagEnabled('useNewGraphQLClient')) {
      return new DelegatingGraphQLClient();
    }
    return new DelegatingApolloClient();
  }

  private getBaseUrl() {
    const base = this.origin.toString();
    if (base.endsWith('/')) {
      return base.slice(0, -1);
    }

    return base;
  }

  /**
   * Load organization IDs for the given cloud IDs.
   *
   * @param cloudIds The cloud IDs to fetch
   * @returns An array of organization IDs
   */
  public async loadOrganizationIDs(cloudIds: string[]): Promise<string[]> {
    const client = this.clients.getOrCreate(this.getBaseUrl(), '/gateway/api/graphql');

    const responseOrErr = await client
      .query<OrganizationIdQuery['data'], OrganizationIdQueryInput>({
        query: IdentityQueryOrganizationId.OrganizationId,
        variables: { cloudIds },
      })
      // To catch runtime/network errors
      .catch((err: Error): Error => new IdentityGraphqlFetchError(err.message));

    // To catch GraphQL errors
    throwOnQueryError<OrganizationIdQuery['data']>(responseOrErr);

    return responseOrErr.data.tenantContexts.map((org) => org.orgId) ?? [];
  }

  /**
   * Fetches full identity profiles for the given account IDs.
   *
   * @param accountIds The account ids to fetch
   * @returns An array of full user profiles
   */
  public async loadFullIdentityProfiles(
    accountIds: readonly string[]
  ): Promise<UserProfileIdentity[]> {
    const client = this.clients.getOrCreate(this.getBaseUrl(), '/gateway/api/graphql');

    const responseOrErr = await client
      .query<UsersQuery['data'], UsersQueryInput>({
        query: IdentityQueryUsers.UsersQuery,
        variables: { accountIds },
      })
      // To catch runtime/network errors
      .catch((err: Error): Error => new IdentityGraphqlFetchError(err.message));

    // To catch GraphQL errors
    throwOnQueryError<UsersQuery['data']>(responseOrErr);

    return responseOrErr.data.users ?? [];
  }
}

let identityClient: IdentityClient;

export const getIdentityClient = () => {
  if (!identityClient) {
    identityClient = new IdentityClient();
  }

  return identityClient;
};
